import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Breadcrumbs, CircularProgress, IconButton, Link, Paper, Switch, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ArrowLeft } from 'lucide-react';
import {
  getNotificationPreferences,
  updateNotificationPreference,
  type NotificationPreference,
  type NotificationPreferencesResponse,
} from '@/lib/api/notifications';

type ChannelKey = 'email' | 'inApp' | 'push';

const NOTIFICATION_PREFERENCES_QUERY_KEY = ['notifications', 'preferences'];

const notificationSwitchSx: SxProps<Theme> = {
  width: 42,
  height: 24,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0,
    m: '2px',
    color: '#fff',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        bgcolor: '#13B8A6',
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 20,
    height: 20,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.22)',
    border: 'none',
    bgcolor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: '999px',
    bgcolor: '#DDE3E8',
    opacity: 1,
  },
};

const NotificationPreferencesView: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [lastError, setLastError] = useState<string | null>(null);

  const preferencesQuery = useQuery({
    queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY,
    queryFn: getNotificationPreferences,
  });

  const updatePreferenceMutation = useMutation({
    mutationFn: updateNotificationPreference,
    onMutate: async (input) => {
      setLastError(null);
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY });
      const previous = queryClient.getQueryData<NotificationPreferencesResponse>(NOTIFICATION_PREFERENCES_QUERY_KEY);
      queryClient.setQueryData<NotificationPreferencesResponse>(
        NOTIFICATION_PREFERENCES_QUERY_KEY,
        (current) => ({
          items: (current?.items ?? []).map((preference) =>
            preference.eventKey === input.eventKey
              ? { ...preference, channels: { ...preference.channels, ...input.channels } }
              : preference
          ),
        })
      );
      return { previous };
    },
    onError: (error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATION_PREFERENCES_QUERY_KEY, context.previous);
      }
      setLastError(error instanceof Error ? error.message : 'Could not update notification preference.');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY });
    },
  });

  const preferences = preferencesQuery.data?.items ?? [];

  const toggleChannel = (eventKey: string, channelKey: ChannelKey, checked: boolean) => {
    updatePreferenceMutation.mutate({
      eventKey,
      channels: {
        [channelKey]: checked,
      },
    });
  };

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f7f9fb',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: '1px solid #dbe6eb',
              bgcolor: '#f8fafc',
              px: { xs: 2, sm: 3 },
              py: 1.25,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link component={RouterLink} to="/profile?view=hub" underline="hover" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#64748b' }}>
                Settings
              </Link>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Notification Preferences
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, lg: 3.5 }, pb: { xs: 2.5, sm: 3.5 }, pt: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ mb: { xs: 4, sm: 4.75 }, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <IconButton
              aria-label="Back to settings"
              onClick={() => navigate('/profile?view=hub')}
              sx={{
                mt: 0.3,
                width: 30,
                height: 30,
                color: '#1F2B34',
                p: 0,
                '&:hover': {
                  bgcolor: 'transparent',
                },
              }}
            >
              <ArrowLeft size={28} strokeWidth={2.1} />
            </IconButton>

            <Box sx={{ minWidth: 0, pt: 0.05 }}>
              <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: { xs: '1.6rem', sm: '1.92rem' }, lineHeight: 1.05 }}>
                Notification Preferences
              </Typography>
              <Typography sx={{ mt: 0.65, color: '#676B80', fontSize: { xs: '0.8rem', sm: '0.92rem' }, lineHeight: 1.2 }}>
                Choose events and delivery channels
              </Typography>
            </Box>
          </Box>

          {lastError ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
              {lastError}
            </Alert>
          ) : null}

          <Box
            sx={{
              borderRadius: '14px',
              border: '1px solid transparent',
              borderBottomColor: '#e4ebf1',
              bgcolor: '#fff',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
              pt: { xs: 1, sm: 1.5 },
              pb: { xs: 1, sm: 1.5 },
            }}
          >
            {preferencesQuery.isLoading ? (
              <Box sx={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={26} />
              </Box>
            ) : preferencesQuery.isError ? (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                <Alert severity="error" sx={{ borderRadius: '12px' }}>
                  Could not load notification preferences.
                </Alert>
              </Box>
            ) : preferences.length === 0 ? (
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                <Typography sx={{ color: '#6D7B87', fontSize: '0.96rem' }}>
                  Notification preferences are not available for this account.
                </Typography>
              </Box>
            ) : preferences.map((preference, index) => (
              <Box
                key={preference.eventKey}
                sx={{
                  position: 'relative',
                  px: { xs: 1.25, sm: 2.5 },
                  py: { xs: 1.7, sm: 2.1 },
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) auto' },
                  gap: { xs: 1.15, lg: 1.8 },
                  alignItems: 'center',
                  ...(index > 0 && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: { xs: 20, sm: 28 },
                      right: { xs: 20, sm: 28 },
                      borderTop: '1px solid #e8eef3',
                    },
                  }),
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: '#111A22', fontWeight: 700, fontSize: { xs: '0.98rem', sm: '1rem' } }}>
                    {preference.eventLabel}
                  </Typography>
                  <Typography sx={{ mt: 0.35, color: '#6D7B87', fontSize: '0.82rem', lineHeight: 1.35 }}>
                    {preference.workflow}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.65 }}>
                    <Switch
                      checked={preference.channels.email}
                      disabled={updatePreferenceMutation.isPending}
                      onChange={(_, checked) => toggleChannel(preference.eventKey, 'email', checked)}
                      inputProps={{ 'aria-label': `${preference.eventLabel} email notifications` }}
                      sx={notificationSwitchSx}
                    />
                    <Typography sx={{ color: '#6D7B87', fontSize: '0.96rem' }}>Email</Typography>
                  </Box>

                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.65 }}>
                    <Switch
                      checked={preference.channels.inApp}
                      disabled={updatePreferenceMutation.isPending}
                      onChange={(_, checked) => toggleChannel(preference.eventKey, 'inApp', checked)}
                      inputProps={{ 'aria-label': `${preference.eventLabel} web app notifications` }}
                      sx={notificationSwitchSx}
                    />
                    <Typography sx={{ color: '#6D7B87', fontSize: '0.96rem' }}>Web App</Typography>
                  </Box>

                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.65 }}>
                    <Switch
                      checked={preference.channels.push}
                      disabled={updatePreferenceMutation.isPending}
                      onChange={(_, checked) => toggleChannel(preference.eventKey, 'push', checked)}
                      inputProps={{ 'aria-label': `${preference.eventLabel} push notifications` }}
                      sx={notificationSwitchSx}
                    />
                    <Typography sx={{ color: '#6D7B87', fontSize: '0.96rem' }}>Push</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default NotificationPreferencesView;
